// Pago en curso del widget de Line, guardado para sobrevivir a una recarga.
//
// El `payment_id` que devuelve el checkout vivía solo en el estado de React. En
// iPhone, el salto a la app del banco (3DS) descarta la pestaña; al volver, la
// página se recarga, el id se perdió y el portal creaba un checkout NUEVO: el
// pago aprobado quedaba huérfano y al usuario se le pedía pagar otra vez.
//
// Con el id guardado, al volver a montar la pantalla lo primero que se hace es
// CONFIRMAR ese pago; recién si no está aprobado se ofrece pagar.
//
// Dos copias, con el mismo vencimiento:
//   - sessionStorage: el caso común (misma pestaña, recarga).
//   - localStorage:   iOS a veces mata la pestaña entera y el usuario abre una
//                     nueva; ahí sessionStorage ya no está.
// El vencimiento es corto (2 h) a propósito: pasado ese rato, un pago que quedó
// sin confirmar lo levanta el barrido de reconciliación del backend, y el
// checkout mismo responde "ya está activo" si encuentra un pago aprobado.
//
// La clave va atada a quién paga: al `pt` del link de pago o, sin `pt`, al
// partner + sujeto de la sesión. Así el pago de una cuenta nunca se le ofrece a
// otra que use el mismo navegador. El `pt` es una credencial (7 días): en la
// clave va su huella, no el token, para no dejarlo persistido en localStorage.
//
// Lo guardado NO es una credencial: un `payment_id` solo sirve presentado junto
// con el `pt` o la sesión del titular, que es lo que valida el backend.

const PREFIX = 'authsys:checkout-resume:';

/** Vida útil del pago guardado, en las dos copias. */
export const RESUME_MAX_AGE_MS = 2 * 60 * 60 * 1000;

// Tolerancia a un reloj que retrocede (ajuste de hora del dispositivo).
const CLOCK_SKEW_MS = 5 * 60 * 1000;

export interface CheckoutResume {
  paymentId: string;
  planId: string;
  checkoutUrl: string;
  /** Epoch ms del momento en que el checkout devolvió el payment_id. */
  createdAt: number;
}

// FNV-1a de 32 bits. No es criptografía: alcanza para que la clave no sea el
// token y para que dos `pt` distintos no compartan casillero.
function fnv1a(input: string, seed: number): string {
  let h = seed >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

function fingerprint(input: string): string {
  return fnv1a(input, 0x811c9dc5) + fnv1a(input, 0x9747b28c);
}

/**
 * Clave de storage del pago en curso. Con `pt` manda el `pt`; sin él, partner +
 * sujeto (user_id o subscription_id, lo que la pantalla conozca). Devuelve null
 * si no hay con qué atarlo: en ese caso no se persiste nada.
 */
export function resumeKey(scope: {
  partnerId: string;
  pt?: string | null;
  subject?: string | null;
}): string | null {
  if (scope.pt) return `${PREFIX}pt:${fingerprint(scope.pt)}`;
  if (scope.partnerId && scope.subject) return `${PREFIX}u:${scope.partnerId}:${scope.subject}`;
  return null;
}

/**
 * Interpreta lo guardado. Devuelve null si está corrupto, incompleto o vencido.
 * Pura: recibe el `now` para poder probar el vencimiento.
 */
export function parseResume(raw: string | null | undefined, now: number): CheckoutResume | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const p = parsed as Record<string, unknown>;
  const { paymentId, planId, checkoutUrl, createdAt } = p;
  if (typeof paymentId !== 'string' || !paymentId) return null;
  if (typeof planId !== 'string' || !planId) return null;
  if (typeof checkoutUrl !== 'string' || !checkoutUrl) return null;
  if (typeof createdAt !== 'number' || !Number.isFinite(createdAt)) return null;
  const age = now - createdAt;
  if (age > RESUME_MAX_AGE_MS || age < -CLOCK_SKEW_MS) return null;
  return { paymentId, planId, checkoutUrl, createdAt };
}

// Los storages disponibles, en orden de preferencia. El acceso mismo puede
// tirar (Safari privado, cookies bloqueadas): cada uno va en su try/catch.
function storages(): Storage[] {
  const out: Storage[] = [];
  try {
    if (sessionStorage) out.push(sessionStorage);
  } catch {
    // sin sessionStorage
  }
  try {
    if (localStorage) out.push(localStorage);
  } catch {
    // sin localStorage
  }
  return out;
}

/** Guarda el pago en curso apenas el checkout devuelve el `payment_id`. */
export function saveCheckoutResume(
  key: string | null,
  payment: Omit<CheckoutResume, 'createdAt'>,
  now: number = Date.now(),
): void {
  if (!key) return;
  const payload: CheckoutResume = { ...payment, createdAt: now };
  const raw = JSON.stringify(payload);
  for (const s of storages()) {
    try {
      s.setItem(key, raw);
    } catch {
      // Storage lleno o bloqueado: sin copia, una recarga vuelve al paso de
      // pago como antes. Degradación, no error.
    }
  }
}

/**
 * Lee el pago en curso para esta clave. Primero la pestaña; si no está, la
 * copia de localStorage. Lo vencido o corrupto se borra al pasar.
 */
export function readCheckoutResume(key: string | null, now: number = Date.now()): CheckoutResume | null {
  if (!key) return null;
  for (const s of storages()) {
    try {
      const raw = s.getItem(key);
      if (raw === null) continue;
      const resume = parseResume(raw, now);
      if (resume) return resume;
      s.removeItem(key);
    } catch {
      // seguimos con el próximo storage
    }
  }
  return null;
}

/** Borra el pago en curso: al activar, al confirmarse rechazado, o al abandonarlo. */
export function clearCheckoutResume(key: string | null): void {
  if (!key) return;
  for (const s of storages()) {
    try {
      s.removeItem(key);
    } catch {
      // nada que limpiar
    }
  }
}
